import { Avatar, Button, Tag } from '@ads/components-react';
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDownloadDeclaration } from '../../../hooks/useDownloadDeclaration';
import type { StudentEnrollmentOption } from '../../../hooks/useStudentEnrollmentSearch';
import { SectionCard } from '../../molecules/SectionCard';
import { StudentSearchAutocomplete } from '../../molecules/StudentSearchAutocomplete';
import { StudentGradeDetailCard } from '../StudentGradeDetailCard';

/** Iniciais (até 2 letras) a partir do nome, para o fallback do Avatar. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Painel da aba **Resumo**: busca um aluno da turma (por nome ou e-mail) e baixa a
 * declaração de notas e frequência em PDF.
 *
 * Orquestra o autocomplete de matrículas ({@link StudentSearchAutocomplete}) e o download
 * ({@link useDownloadDeclaration}); a seleção corrente é estado local efêmero. Alterna entre
 * o estado vazio (nenhum aluno) e o card do aluno selecionado com a ação de download.
 */
export function StudentDeclarationPanel() {
  const { t } = useTranslation('gradeAttendanceConfig');
  const [selected, setSelected] = useState<StudentEnrollmentOption | null>(null);
  const { download, isDownloading } = useDownloadDeclaration();

  const handleDownload = () => {
    if (!selected) return;
    const suffix = selected.classCode || selected.courseRegistrationId;
    download(selected.courseRegistrationId, `declaracao-notas-frequencia-${suffix}.pdf`);
  };

  return (
    <div className="flex flex-col gap-xxs">
      <SectionCard
        title={t('declaration.title')}
        icon={<DocumentTextIcon className="size-xxs text-ctx-content-base" />}
      >
        <div className="flex flex-col gap-xxs px-xxxs py-mini">
          <p className="text-ctx-content-base ts-paragraph-xs">{t('declaration.description')}</p>

          <div className="md:max-w-[50%]">
            <StudentSearchAutocomplete
              selectedId={selected?.courseRegistrationId ?? ''}
              selectedLabel={selected?.name ?? ''}
              onSelect={setSelected}
              label={t('declaration.searchLabel')}
              placeholder={t('declaration.searchPlaceholder')}
            />
          </div>

          {selected ? (
            <div className="flex items-center justify-between gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-body p-mini">
              <div className="flex min-w-none items-center gap-micro">
                <Avatar size="md" fallback={getInitials(selected.name)} />
                <div className="flex min-w-none flex-col gap-quark">
                  <span className="truncate text-ctx-content-title ts-subtitle-xs">
                    {selected.name}
                  </span>
                  <div className="flex items-center gap-nano">
                    {selected.email ? (
                      <span className="truncate text-ctx-content-base ts-paragraph-xs">
                        {selected.email}
                      </span>
                    ) : null}
                    {selected.classCode ? (
                      <Tag label={selected.classCode} size="sm" custom={{ text: 'text-nowrap' }} />
                    ) : null}
                  </div>
                </div>
              </div>

              <Button
                hierarchy="primary"
                size="md"
                leadingIcon={ArrowDownTrayIcon}
                isLoading={isDownloading}
                onClick={handleDownload}
              >
                {t('declaration.download')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-micro px-xxs py-xl text-center">
              <span className="flex size-xl items-center justify-center rounded-pill bg-base-gray-100">
                <MagnifyingGlassIcon className="size-xs text-base-gray-500" />
              </span>
              <span className="text-ctx-content-title ts-subtitle-xs">
                {t('declaration.emptyTitle')}
              </span>
              <p className="max-w-[24rem] text-ctx-content-base ts-paragraph-xs">
                {t('declaration.emptyDesc')}
              </p>
            </div>
          )}
        </div>
      </SectionCard>

      {selected ? (
        <StudentGradeDetailCard courseRegistrationId={Number(selected.courseRegistrationId)} />
      ) : null}
    </div>
  );
}
